import type { ButtonHTMLAttributes, ReactNode } from 'react';

export interface ClearFiltersButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** When false, the button is hidden (no active filters). Default true. */
  visible?: boolean;
  /** Optional custom label. Default: "Clear Filters" */
  label?: ReactNode;
  /** Visual density for toolbar vs empty-state placement. */
  variant?: 'toolbar' | 'inline' | 'empty';
}

const VARIANT_CLASS: Record<NonNullable<ClearFiltersButtonProps['variant']>, string> = {
  toolbar:
    'inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-[#0088cc] border border-[#0088cc]/25 bg-[#0088cc]/5 hover:bg-[#0088cc]/10 hover:border-[#0088cc]/40 transition-colors whitespace-nowrap',
  inline:
    'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#0088cc] hover:text-[#0077b3] hover:underline transition-colors whitespace-nowrap',
  empty:
    'mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#0088cc] hover:underline transition-colors',
};

/**
 * Shared “Clear Filters” control for list/filter toolbars across portals.
 * Parent owns filter state and passes `onClick` + `visible` (active filters).
 */
export function ClearFiltersButton({
  visible = true,
  label = 'Clear Filters',
  variant = 'toolbar',
  type = 'button',
  className,
  ...rest
}: ClearFiltersButtonProps) {
  if (!visible) return null;

  return (
    <button
      type={type}
      className={[VARIANT_CLASS[variant], className].filter(Boolean).join(' ')}
      {...rest}
    >
      {label}
    </button>
  );
}
